import { forwardRef, useCallback } from 'react'

import { css } from 'twind/css'

import { AccessibleIcon } from '@core/components/AccessibleIcon/AccessibleIcon'
import { useTwindContext } from '@core/twind'

export interface ControlsButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  badge?: boolean
  size?: number
  imgSize?: number
  src: string | React.ReactNode
  hideImage?: boolean
  highlighted?: boolean
}

/**
 * Square button used in Player UI (e.g. left caret, X for close, kebab menu).
 * Adds plain circle "badge" and wraps <AccessibleIcon />.
 */
export const ControlsButton = forwardRef<
  HTMLButtonElement,
  ControlsButtonProps
>(
  (
    {
      badge = false,
      size = 40,
      imgSize,
      src,
      hideImage,
      className,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      highlighted,
      ...rest
    },
    ref,
  ) => {
    const { tw } = useTwindContext()

    /**
     * on android devices we want to hide outline when mouse/touch even used and
     * keep it for keyboard users, instead of removing outline we blur when
     * mouse/touch used
     */
    const handleBlur = useCallback((event) => {
      setTimeout(() => {
        event.target.blur()
      }, 0)
    }, [])

    const buttonStyle = css({
      flex: `0 0 ${size}`,
      height: `${size}px`,
      width: `${size}px`,
      '&:disabled': {
        cursor: 'not-allowed',
      },
    })

    const button = tw?.(
      buttonStyle,
      'items-center',
      'bg-none',
      'bg-transparent',
      'border-none',
      'flex',
      'justify-center',
      'm-1',
      'p-0',
      'pointer-events-auto',
      'relative',
      'cursor-pointer',
    )

    const badgeStyle = css({
      lineHeight: 0,
      background: 'var(--badge-highlight)',
    })

    return (
      <button
        ref={ref}
        {...rest}
        aria-label={rest['aria-label']}
        onMouseUp={handleBlur}
        className={`${className} ${button}`}
      >
        {!hideImage && src && (
          <AccessibleIcon
            src={src}
            label={''}
            width={`${imgSize || size}px`}
            height={`${imgSize || size}px`}
          />
        )}
        {badge && (
          <div
            className={tw?.(
              badgeStyle,
              'absolute',
              'top-0',
              'right-0',
              'h-2.5',
              'w-2.5',
              'rounded-full',
            )}
          />
        )}
      </button>
    )
  },
)
